import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { arrayMoveImmutable } from 'array-move';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { utils, writeFile } from 'xlsx';
import { episodeApi } from 'apis/story/episodeApis';
import 'utils/style/sortDragging.css';
import EpisodeRegisterModal from './EpisodeRegisterModal';
import SearchInput from 'components/search/SearchInput';
import MainTable from 'components/table/MainTable';
import TableToolbar from 'components/table/TableToolbar';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DeleteModal from 'components/modal/DeleteModal';
import { DocumentText1, Edit2, ReceiveSquare, Trash } from 'iconsax-react';
import ActionButton from 'components/table/ActionButton';
import { fetchScriptData, sheetColumns } from '../script/ScriptHelper';
import { IEpisode, IProject } from 'utils/types/storyTypes';
import DefaultButton from 'components/button/DefaultButton';
import { ColumnsType } from 'antd/es/table';

/**
 * * 작품 관리 - 에피소드 조회 (& 오토메)
 */

interface IProps {
  selectProject: IProject;
  projectId?: string;
}

const EpisodeTable = ({ selectProject, projectId }: IProps) => {
  const { t } = useTranslation();
  const [cookies] = useCookies();
  const { lang } = cookies;
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState();
  const [visible, setVisible] = useState(false);
  const [episodeList, setEpisodeList] = useState<IEpisode[]>([]); // 에피소드 목록

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState<any>(null);

  /// 데이터 불러오기
  const fetchData = async () => {
    const resEpisodeList = await episodeApi.list(projectId);

    setEpisodeList(resEpisodeList.episodes);
  };

  useEffect(() => {
    fetchData().catch(console.log);
  }, [lang]);

  /// 모달 오픈
  const handleOpen = () => {
    setVisible(true);
  };

  /// 수정 페이지 이동
  const handleUpdateClick = (record: IEpisode) => {
    navigate(`${record?.episode_id}/update`, {
      state: {
        projectType: selectProject?.project_type,
        record: record,
        lang: lang,
        dataList: episodeList,
      },
    });
  };

  /// 스크립트로 이동
  const handleScriptClick = (projectId) => {
    // 새 창으로 열기 위해 window.open()을 사용
    window.open(
      `/sheet/${projectId.project_id}/contents/${projectId.episode_id}/?title=${
        selectProject?.title
      }&episode=${projectId.indexed_title}&lang=${
        lang !== undefined ? lang : 'KO' // 언어 기본값은 KO
      }`,
    );
  };

  /// 에피소드 삭제
  const handleDelete = async () => {
    if (selectedEpisode) {
      try {
        setDeleteLoading(true);
        await episodeApi.delete(
          selectedEpisode.project_id,
          selectedEpisode.episode_id,
        );
        fetchData();
        message.success(
          `${selectedEpisode.indexed_title} 가(이) 삭제되었습니다.`,
        );
        setDeleteLoading(false);
        setDeleteModal(false);
      } catch (err) {
        setDeleteLoading(false);
      }
    }
  };

  /// 스크립트 다운로드
  const handleClickDownload = async (record) => {
    console.log(`## handleClickDownload in EpisodeTable`);

    // 스크립트 데이터 불러오기
    const scripts = await fetchScriptData(
      record.project_id,
      record.episode_id,
      lang,
    );

    // 컬럼명 배열에 답기
    const columnArrayName: string[] = [];
    const columnArrayTitle: string[] = [];
    sheetColumns.forEach((v) => {
      columnArrayName.push(v.name);
      columnArrayTitle.push(v.title);
    });

    // header에 컬럼 순서를 지정해줌
    const ws = utils.json_to_sheet(scripts, {
      header: columnArrayName,
    });
    const wb = utils.book_new();

    // 컬럼을 한글로 바꾸자
    columnArrayTitle.forEach((v, i) => {
      const cellAdd = utils.encode_cell({ c: i, r: 0 });
      ws[cellAdd].v = v;
    });

    console.log(ws);

    // 에피소드 id, 프로젝트 id 열 숨기기
    ws['!cols'] = [];
    ws['!cols'][3] = { wch: 60 };
    ws['!cols'][6] = { wch: 30 }; // 케릭터 모션
    ws['!cols'][17] = { hidden: true };
    ws['!cols'][18] = { hidden: true };
    ws['!cols'][19] = { hidden: true };
    ws['!cols'][20] = { hidden: true };
    ws['!cols'][21] = { hidden: true };
    ws['!cols'][22] = { hidden: true };
    ws['!cols'][23] = { hidden: true };
    ws['!cols'][24] = { hidden: true };

    // 현재 시간 출력
    const today = dayjs().format('MMDD_HHmmss');
    utils.book_append_sheet(wb, ws, 'SHEET JS');
    writeFile(wb, `${record.indexed_title}_${today}.xlsx`);
  };

  /// 정렬 드래깅이 끝날 때 => api 호출
  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        [...episodeList],
        oldIndex,
        newIndex,
      ).filter((el) => !!el);
      // console.log('Sorted items >>>>> >> ', newData);
      const sortData: (IEpisode & { sortKey: number })[] = newData.map(
        (v, i) => {
          return {
            ...v,
            sortKey: i,
          };
        },
      );

      const response = await episodeApi.sorting(
        selectProject.project_id,
        sortData,
      );

      if (!response) {
        return false;
      }

      setEpisodeList(response.episodes);
      message.success('에피소드가 재정렬 되었습니다.');
    }
  };
  const DraggableContainer = (props) => {
    return (
      <SortableContainerInstance
        useDragHandle
        disableAutoscroll
        helperClass='row-dragging' // 정렬 아이콘 클릭 된 상태로 움직일 때의 모습 스타일링
        onSortEnd={onSortEnd}
        {...props}
      />
    );
  };
  const DraggableBodyRow = (restProps) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    // ***** int로 변환해주었어야 했다!
    const index = episodeList.findIndex((item) =>
      typeof item.episode_id === 'string'
        ? parseInt(item.episode_id)
        : item.episode_id === parseInt(restProps['data-row-key']),
    );
    return <SortableItem index={index} {...restProps} />;
  };

  const tableActionMenu = (record: IEpisode) => {
    return [
      {
        key: 1,
        item: (
          <DocumentText1 size='24' onClick={() => handleScriptClick(record)} />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <ReceiveSquare
            size='24'
            onClick={() => handleClickDownload(record)}
          />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 3,
        item: <Edit2 size='24' onClick={() => handleUpdateClick(record)} />,
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },
      {
        key: 4,
        item: (
          <Trash
            size='24'
            onClick={() => {
              setSelectedEpisode(record);
              setDeleteModal(true);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
      {
        key: 5,
        item: <DragHandle />,
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const column: ColumnsType<IEpisode> = [
    {
      title: 'ID',
      dataIndex: 'episode_id',
      width: '6rem',
      align: 'center',
    },

    {
      title: (
        <p>{t('storyEpisodeID.storyTabMenuList.episodeContent.table.title')}</p>
      ),
      dataIndex: 'indexed_title',
      render: (text, record) => <p>{record.indexed_title}</p>,
    },

    {
      title: (
        <p>{t('storyEpisodeID.storyTabMenuList.episodeContent.table.type')}</p>
      ),
      dataIndex: 'episode_type',
      render: (text) =>
        text === 'chapter' ? (
          <span>에피소드</span>
        ) : text === 'side' ? (
          <span>사이드</span>
        ) : (
          text === 'ending' && <span>엔딩</span>
        ),
      // filters: EpisodeTypeList,
      // // indexOf(value) 값이 있으면 true=0
      // onFilter: (value, record) => record.episode_type.indexOf(value) === 0,
    },
    {
      title: (
        <p>
          {t(
            'storyEpisodeID.storyTabMenuList.episodeContent.table.connectEpisode',
          )}
        </p>
      ),
      dataIndex: 'depend_episode',
      render: (_) => {
        return <p>{_}</p>;
      },
    },

    {
      title: (
        <p>
          {t('storyEpisodeID.storyTabMenuList.episodeContent.table.status')}
        </p>
      ),
      dataIndex: 'episode_status',
      width: '6rem',
      render: (_) => {
        return <p>{_}</p>;
      },
    },
    {
      title: '',
      width: '5rem',
      align: 'center',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
  ];

  return (
    <>
      <TableToolbar>
        <SearchInput
          placeholder={t(
            'storyEpisodeID.storyTabMenuList.episodeContent.header.searchInputPlaceholder',
          )}
          setSearchData={setSearchData}
        />
        <BtnsWrapper>
          <DefaultButton name='register' onClick={handleOpen}>
            {t(
              'storyEpisodeID.storyTabMenuList.episodeContent.header.registerEpisode',
            )}
          </DefaultButton>
        </BtnsWrapper>
      </TableToolbar>
      <MainTable
        columns={column}
        dataSource={
          searchData
            ? episodeList?.filter(
                (data) => data.title.indexOf(searchData) !== -1,
              )
            : episodeList
        }
        isPagination={false}
        rowKey={(record) => `${record.episode_id}`}
        rowClassName={(record) => record.episode_type === 'side' && 'red'}
        components={
          episodeList && {
            body: {
              // style: { color: 'blue' },
              wrapper: DraggableContainer,
              row: DraggableBodyRow,
            },
          }
        }
      />

      {/* 등록 모달 */}
      <EpisodeRegisterModal
        visible={visible}
        setVisible={setVisible}
        lang={cookies.lang}
        fetchData={fetchData}
      />
      <DeleteModal
        visible={deleteModal}
        setVisible={setDeleteModal}
        title={t(
          'storyEpisodeID.storyTabMenuList.episodeContent.table.deleteMessage',
        )}
        label={selectedEpisode?.indexed_title}
        handleDelete={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

export default EpisodeTable;

// 정렬 아이콘
const DragHandle = SortableHandle(() => (
  <MenuOutlined style={{ cursor: 'pointer', color: '#999' }} />
));
const SortableItem = SortableElement((props) => <tr {...props} />);
const SortableContainerInstance = SortableContainer((props) => (
  <tbody {...props} />
));

const BtnsWrapper = styled.div`
  display: flex;
  align-items: center;
  & > * + * {
    margin-left: 16px;
  }
`;
